import { ApiFormattedResponse } from "@models/api_formatted_response"
import {
  Changelog,
  ChangelogCategory,
  ChangelogMedia,
  ChangelogPaginate,
  ChangelogSettings,
  ChangelogSubscriberPaginate,
  ChangelogDeliveryPage,
  CreateChangelogCategoryInput,
  CreateChangelogInput,
  DeleteChangelogCategoryInput,
  ListChangelogInput,
  ListChangelogSubscribersInput,
  ListDeliveriesInput,
  PublishChangelogInput,
  ScheduleChangelogInput,
  UpdateChangelogCategoryInput,
  UpdateChangelogInput,
  UpdateChangelogSettingsInput,
  WhatsAppBusinessAccount,
  WhatsAppBusinessTemplate,
  ChangelogWhatsappType
} from "@models/changelog"
import { mutate, query } from "@utils/graphql"
import archiveAnnouncement from "__generated__/queries/mutations/archiveAnnouncement"
import unarchiveAnnouncement from "__generated__/queries/mutations/unarchiveAnnouncement"
import unpublishAnnouncement from "__generated__/queries/mutations/unpublishAnnouncement"
import createAnnouncement from "__generated__/queries/mutations/createAnnouncement"
import createAnnouncementCategory from "__generated__/queries/mutations/createAnnouncementCategory"
import deleteAnnouncement from "__generated__/queries/mutations/deleteAnnouncement"
import deleteAnnouncementCategory from "__generated__/queries/mutations/deleteAnnouncementCategory"
import publishAnnouncement from "__generated__/queries/mutations/publishAnnouncement"
import scheduleAnnouncement from "__generated__/queries/mutations/scheduleAnnouncement"
import updateAnnouncement from "__generated__/queries/mutations/updateAnnouncement"
import updateAnnouncementCategory from "__generated__/queries/mutations/updateAnnouncementCategory"
import updateChangelogSettings from "__generated__/queries/mutations/updateChangelogSettings"
import translateAnnouncement from "__generated__/queries/mutations/translateAnnouncement"
import uploadAnnouncementMedia from "__generated__/queries/mutations/uploadAnnouncementMedia"
import getAdminAnnouncements from "__generated__/queries/queries/getAdminAnnouncements"
import getAnnouncementCategories from "__generated__/queries/queries/getAnnouncementCategories"
import getChangelogDeliveries from "__generated__/queries/queries/getChangelogDeliveries"
import getChangelogSettings from "__generated__/queries/queries/getChangelogSettings"
import getChangelogSubscribers from "__generated__/queries/queries/getChangelogSubscribers"
import getChangelogWhatsAppAccounts from "__generated__/queries/queries/getChangelogWhatsAppAccounts"
import getChangelogWhatsAppTemplates from "__generated__/queries/queries/getChangelogWhatsAppTemplates"

type Envelope<T> = {
  success?: boolean
  data?: T
  error?: boolean
  message?: string
}

// ─── Queries ─────────────────────────────────────────────────────────────────

export async function listAnnouncements(
  input: ListChangelogInput
): Promise<ApiFormattedResponse<ChangelogPaginate>> {
  const response = await query<{
    getAdminAnnouncements?: Envelope<ChangelogPaginate>
  }>({
    query: getAdminAnnouncements,
    fetchPolicy: "network-only",
    variables: { input }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return { isSuccess: true, data: response?.data?.getAdminAnnouncements?.data }
}

export async function listCategories(): Promise<
  ApiFormattedResponse<ChangelogCategory[]>
> {
  const response = await query<{
    getAnnouncementCategories?: Envelope<ChangelogCategory[]>
  }>({ query: getAnnouncementCategories, fetchPolicy: "network-only" })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.getAnnouncementCategories?.data
  }
}

export async function getSettings(): Promise<
  ApiFormattedResponse<ChangelogSettings>
> {
  const response = await query<{
    getChangelogSettings?: Envelope<ChangelogSettings>
  }>({ query: getChangelogSettings, fetchPolicy: "network-only" })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return { isSuccess: true, data: response?.data?.getChangelogSettings?.data }
}

export async function listDeliveries(
  input: ListDeliveriesInput
): Promise<ApiFormattedResponse<ChangelogDeliveryPage>> {
  const response = await query<{
    getChangelogDeliveries?: Envelope<ChangelogDeliveryPage>
  }>({
    query: getChangelogDeliveries,
    fetchPolicy: "network-only",
    variables: { input }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.getChangelogDeliveries?.data
  }
}

export async function listSubscribers(
  input: ListChangelogSubscribersInput
): Promise<ApiFormattedResponse<ChangelogSubscriberPaginate>> {
  const response = await query<{
    getChangelogSubscribers?: Envelope<ChangelogSubscriberPaginate>
  }>({
    query: getChangelogSubscribers,
    fetchPolicy: "network-only",
    variables: { input }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.getChangelogSubscribers?.data
  }
}

export async function listWhatsAppAccounts(): Promise<
  ApiFormattedResponse<WhatsAppBusinessAccount[]>
> {
  const response = await query<{
    getChangelogWhatsAppAccounts?: Envelope<WhatsAppBusinessAccount[]>
  }>({ query: getChangelogWhatsAppAccounts, fetchPolicy: "network-only" })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.getChangelogWhatsAppAccounts?.data
  }
}

export async function listWhatsAppTemplates(input: {
  type: ChangelogWhatsappType
  accountId: string
}): Promise<ApiFormattedResponse<WhatsAppBusinessTemplate[]>> {
  const response = await query<{
    getChangelogWhatsAppTemplates?: Envelope<WhatsAppBusinessTemplate[]>
  }>({
    query: getChangelogWhatsAppTemplates,
    fetchPolicy: "network-only",
    variables: { input }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.getChangelogWhatsAppTemplates?.data
  }
}

// ─── Changelog mutations ──────────────────────────────────────────────────

export async function createAnnouncementReq(
  input: CreateChangelogInput
): Promise<ApiFormattedResponse<Changelog>> {
  const response = await mutate<{
    createAnnouncement?: Envelope<Changelog>
  }>({
    mutation: createAnnouncement,
    variables: { input }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return { isSuccess: true, data: response?.data?.createAnnouncement?.data }
}

export async function updateAnnouncementReq(
  input: UpdateChangelogInput
): Promise<ApiFormattedResponse<Changelog>> {
  const response = await mutate<{
    updateAnnouncement?: Envelope<Changelog>
  }>({
    mutation: updateAnnouncement,
    variables: { input }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return { isSuccess: true, data: response?.data?.updateAnnouncement?.data }
}

export async function deleteAnnouncementReq(
  _id: string
): Promise<ApiFormattedResponse<Changelog>> {
  const response = await mutate<{
    deleteAnnouncement?: Envelope<Changelog>
  }>({
    mutation: deleteAnnouncement,
    variables: { input: { _id } }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return { isSuccess: true, data: response?.data?.deleteAnnouncement?.data }
}

export async function publishAnnouncementReq(
  input: PublishChangelogInput
): Promise<ApiFormattedResponse<Changelog>> {
  const response = await mutate<{
    publishAnnouncement?: Envelope<Changelog>
  }>({ mutation: publishAnnouncement, variables: { input } })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return { isSuccess: true, data: response?.data?.publishAnnouncement?.data }
}

export async function scheduleAnnouncementReq(
  input: ScheduleChangelogInput
): Promise<ApiFormattedResponse<Changelog>> {
  const response = await mutate<{
    scheduleAnnouncement?: Envelope<Changelog>
  }>({ mutation: scheduleAnnouncement, variables: { input } })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return { isSuccess: true, data: response?.data?.scheduleAnnouncement?.data }
}

export async function archiveAnnouncementReq(
  _id: string
): Promise<ApiFormattedResponse<Changelog>> {
  const response = await mutate<{
    archiveAnnouncement?: Envelope<Changelog>
  }>({ mutation: archiveAnnouncement, variables: { input: { _id } } })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return { isSuccess: true, data: response?.data?.archiveAnnouncement?.data }
}

export async function unarchiveAnnouncementReq(
  _id: string
): Promise<ApiFormattedResponse<Changelog>> {
  const response = await mutate<{
    unarchiveAnnouncement?: Envelope<Changelog>
  }>({ mutation: unarchiveAnnouncement, variables: { input: { _id } } })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return { isSuccess: true, data: response?.data?.unarchiveAnnouncement?.data }
}

export async function unpublishAnnouncementReq(
  _id: string
): Promise<ApiFormattedResponse<Changelog>> {
  const response = await mutate<{
    unpublishAnnouncement?: Envelope<Changelog>
  }>({ mutation: unpublishAnnouncement, variables: { input: { _id } } })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return { isSuccess: true, data: response?.data?.unpublishAnnouncement?.data }
}

export type ChangelogTranslationMap = Record<
  string,
  { title: string; content: string; shortDescription?: string }
>

export async function translateAnnouncementReq(input: {
  title: string
  content: string
  shortDescription?: string
  targetLanguages: string[]
}): Promise<ApiFormattedResponse<ChangelogTranslationMap>> {
  const response = await mutate<{
    translateAnnouncement?: Envelope<ChangelogTranslationMap>
  }>({ mutation: translateAnnouncement, variables: { input } })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return { isSuccess: true, data: response?.data?.translateAnnouncement?.data }
}

export async function uploadAnnouncementMediaReq(
  file: File
): Promise<ApiFormattedResponse<ChangelogMedia>> {
  const response = await mutate<{
    uploadAnnouncementMedia?: Envelope<ChangelogMedia>
  }>({ mutation: uploadAnnouncementMedia, variables: { input: { file } } })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.uploadAnnouncementMedia?.data
  }
}

// ─── Category mutations ──────────────────────────────────────────────────────

export async function createCategoryReq(
  input: CreateChangelogCategoryInput
): Promise<ApiFormattedResponse<ChangelogCategory>> {
  const response = await mutate<{
    createAnnouncementCategory?: Envelope<ChangelogCategory>
  }>({ mutation: createAnnouncementCategory, variables: { input } })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.createAnnouncementCategory?.data
  }
}

export async function updateCategoryReq(
  input: UpdateChangelogCategoryInput
): Promise<ApiFormattedResponse<ChangelogCategory>> {
  const response = await mutate<{
    updateAnnouncementCategory?: Envelope<ChangelogCategory>
  }>({ mutation: updateAnnouncementCategory, variables: { input } })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.updateAnnouncementCategory?.data
  }
}

export async function deleteCategoryReq(
  input: DeleteChangelogCategoryInput
): Promise<ApiFormattedResponse<ChangelogCategory>> {
  const response = await mutate<{
    deleteAnnouncementCategory?: Envelope<ChangelogCategory>
  }>({ mutation: deleteAnnouncementCategory, variables: { input } })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.deleteAnnouncementCategory?.data
  }
}

// ─── Settings mutation ───────────────────────────────────────────────────────

export async function updateSettingsReq(
  input: UpdateChangelogSettingsInput
): Promise<ApiFormattedResponse<ChangelogSettings>> {
  const response = await mutate<{
    updateChangelogSettings?: Envelope<ChangelogSettings>
  }>({ mutation: updateChangelogSettings, variables: { input } })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.updateChangelogSettings?.data
  }
}
